/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { SurveyFormat as SurveyFormatType } from '~lib/generatedGqlTypes';
import { AppDispatch, RootState } from '~app/store';
import { setFocus, scrollIntoView } from './utils';
import { SurveyFormat } from './constants';

// ========== INITIAL STATE

export type SurveyFormatState = {
  /**
   * The currently registered views (eg. questions, page navigation, page description).
   * Used for determining the currently active view, setting the next view active, scrolling to a view, etc.
   *  */
  views: string[];
  /** The survey format (eg. classic or OQAAT) */
  format: SurveyFormatType;
  /** The currently active view - triggers style changes and affects navigation */
  active: string | null;
  /**
   * Whether there is currently a scroll animation occurring.
   * Used in ActiveViewScrollManager to prevent multiple scroll animations from occurring at the same time, as well as preventing unnecessary calculations on scroll.
   *  */
  isAnimating: boolean;
};

export const initialState: SurveyFormatState = {
  views: [],
  format: SurveyFormat.ONE_QUESTION_AT_A_TIME,
  active: null,
  isAnimating: false,
};

// ========== SLICE

export const surveyFormatSlice = createSlice({
  name: 'surveyFormat', // what will show up in the redux dev tools (eg. "surveyFormat/register")
  initialState,
  reducers: {
    register: (state, action: PayloadAction<string>) => {
      state.views.push(action.payload);
    },
    unregister: (state, action: PayloadAction<string>) => {
      state.views = state.views.filter(v => v !== action.payload);
    },
    setFormat: (state, action: PayloadAction<SurveyFormatType>) => {
      state.format = action.payload;
    },
    setActive: (state, { payload: name }: PayloadAction<string | null>) => {
      if (name !== null) {
        state.active = state.views.find(v => v === name) ?? null;
      }
    },
    setAnimating: (state, action: PayloadAction<boolean>) => {
      state.isAnimating = action.payload;
    },
    reset: () => initialState,
  },
});

// ========== ACTIONS

export const { register, unregister, setFormat, setActive, setAnimating, reset } = surveyFormatSlice.actions;

// ========== THUNKS
// The built-in createAsyncThunk() function provides access to the state and dispatch actions via thunkAPI parameter - useful for writing reducers that cause side-effects (eg. dispatching other actions) which wouldn't work correctly in a regular reducer.
// Thunks are typically used for async operations: https://redux-toolkit.js.org/api/createAsyncThunk

let animationCount = 0;

export const scrollToActive = createAsyncThunk('surveyFormat/scrollToActive', async (name: string, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const dispatch = thunkAPI.dispatch as AppDispatch;
  const view = state.surveyFormat.views.find(v => v === name);
  const clickShield = view ? document.getElementById(`view-${view}`) : null;
  if (clickShield) {
    const target = (clickShield.firstElementChild ?? clickShield) as HTMLElement;
    const scrollResult = await scrollIntoView(
      target,
      () => {
        animationCount += 1;
        dispatch(setAnimating(true));
      },
      () => {
        if (animationCount <= 1) {
          dispatch(setAnimating(false));
        }
        animationCount -= 1;
      }
    );
    return scrollResult;
  }
  return `invalid view ${name}`;
});

export const setNextActive = createAsyncThunk('surveyFormat/setNextActive', (_, thunkAPI): void => {
  const state = thunkAPI.getState() as RootState;
  const activeIndex = state.surveyFormat.views.findIndex(v => v === state.surveyFormat.active);
  const nextActive = state.surveyFormat.views[(activeIndex as number) + 1] || null;
  if (nextActive) {
    setFocus(nextActive);
  }
});

// ========== SELECTORS
// These can either be specified here and exported (as below), or accessed directly within components
// eg. const active = useAppSelector(state => state.surveyFormat.active);

export const surveyFormatSelector = (state: RootState): SurveyFormatState => state.surveyFormat;
export const viewsSelector = (state: RootState): string[] => state.surveyFormat.views;
export const formatSelector = (state: RootState): SurveyFormatType => state.surveyFormat.format;
export const activeSelector = (state: RootState): string | null => state.surveyFormat.active;
export const isAnimatingSelector = (state: RootState): boolean => state.surveyFormat.isAnimating;

// ========== EXPORT

export { setFocus };

export default surveyFormatSlice.reducer;
